/**
 * Nested campaigns are Notion-style: they exist only as a mission container.
 * The mission link (`task.nestedSprintId`) is the source of truth.
 * `parentSprintId` / `childOrder` are bookkeeping and must never create a nest
 * that has no mission card in the parent.
 */

export function findLinkingMission(parent, nestedId) {
  if (!parent || !nestedId || !Array.isArray(parent.tasks)) return null;
  return parent.tasks.find((t) => t?.nestedSprintId === nestedId) || null;
}

export function parentHasMissionLink(parent, nestedId) {
  return !!findLinkingMission(parent, nestedId);
}

/**
 * List-API rows are metadata only (no `tasks` array). A parent is trustworthy
 * for the nest invariant only after its full task list has been loaded.
 */
export function isParentTasksAuthoritative(parent) {
  if (!parent || !Array.isArray(parent.tasks)) return false;
  const expectedCount = parent.taskCount;
  if (typeof expectedCount === 'number' && expectedCount > 0 && parent.tasks.length === 0) {
    return false;
  }
  return true;
}

export function findParentByMissionLink(nestedId, allSprints) {
  if (!nestedId) return null;
  for (const candidate of allSprints) {
    if (candidate.deletedAt) continue;
    if (parentHasMissionLink(candidate, nestedId)) return candidate;
  }
  return null;
}

/**
 * True when this campaign is referenced from some parent's mission card
 * or from another campaign in the same persist batch (paste / template tree).
 */
export function hasMissionLinkInTree(nestedId, parentSprint, nestedSprints = []) {
  if (parentHasMissionLink(parentSprint, nestedId)) return true;
  return nestedSprints.some((s) => s?.id !== nestedId && parentHasMissionLink(s, nestedId));
}

/**
 * Walk only mission links. Unlinked `parentSprintId` rows are ignored so
 * template/copy never multiplies sidebar ghosts.
 */
export function collectMissionLinkedDescendants(rootId, allSprints) {
  const byId = new Map(allSprints.map((s) => [s.id, s]));
  const result = [];
  const visited = new Set();

  const walk = (parent) => {
    if (!parent || visited.has(parent.id)) return;
    visited.add(parent.id);
    for (const task of parent.tasks || []) {
      const childId = task?.nestedSprintId;
      if (!childId) continue;
      const child = byId.get(childId);
      if (!child || child.deletedAt || visited.has(child.id)) continue;
      result.push(child);
      walk(child);
    }
  };

  walk(byId.get(rootId));
  return result;
}

/**
 * Campaigns that claim a parent via parentSprintId, whose parent is fully
 * loaded, and that have no mission card in that parent (or any other parent).
 */
/** Parents we still have to full-load before we can tell ghost from real nest. */
export function getUnverifiedNestParentIds(sprints) {
  const lookup = new Map((sprints || []).map((s) => [s.id, s]));
  return [...new Set(
    (sprints || [])
      .filter((s) => s?.parentSprintId && !s.deletedAt)
      .map((s) => s.parentSprintId)
  )].filter((id) => {
    const parent = lookup.get(id);
    return parent && !parent.deletedAt && !isParentTasksAuthoritative(parent);
  });
}

export function findUnlinkedNestedCampaigns(sprints) {
  const unlinked = [];
  for (const sprint of sprints) {
    if (!sprint || sprint.deletedAt || !sprint.parentSprintId) continue;
    if (findParentByMissionLink(sprint.id, sprints)) continue;

    const parent = sprints.find((s) => s.id === sprint.parentSprintId);
    if (!parent || parent.deletedAt) continue;
    if (!isParentTasksAuthoritative(parent)) continue;

    const linkedSibling = (sprints).find((s) =>
      s.id !== sprint.id &&
      !s.deletedAt &&
      (s.parentSprintId === parent.id || findParentByMissionLink(s.id, sprints)?.id === parent.id) &&
      parentHasMissionLink(parent, s.id) &&
      (s.name || '') === (sprint.name || '')
    );

    unlinked.push({
      sprint,
      parent,
      isNameDuplicate: !!linkedSibling,
      linkedSibling: linkedSibling || null,
    });
  }
  return unlinked;
}
